import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Loader2, Mail, Lock, User, ArrowLeft, CheckCircle2, Building2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { usePortalAuth } from '@/contexts/PortalAuthContext';
import { supabase } from '@/integrations/supabase/client';

type AuthStep = 'identification' | 'login' | 'activation' | 'confirmation';

interface CustomerInfo {
  id: string;
  name: string;
  company_id: string;
  company_name: string;
  has_portal_access: boolean;
  onboarding_status: 'pending' | 'completed';
  has_user_id: boolean;
  masked_email?: string;
}

const formatDocument = (value: string): string => {
  const numbers = value.replace(/\D/g, '');
  if (numbers.length <= 11) {
    // CPF: 000.000.000-00
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `${numbers.slice(0, 3)}.${numbers.slice(3)}`;
    if (numbers.length <= 9) return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6)}`;
    return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6, 9)}-${numbers.slice(9, 11)}`;
  } else {
    // CNPJ: 00.000.000/0000-00
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 5) return `${numbers.slice(0, 2)}.${numbers.slice(2)}`;
    if (numbers.length <= 8) return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5)}`;
    if (numbers.length <= 12) return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5, 8)}/${numbers.slice(8)}`;
    return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5, 8)}/${numbers.slice(8, 12)}-${numbers.slice(12, 14)}`;
  }
};

const PortalAuth: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, portalUser, signIn, loading: authLoading } = usePortalAuth();
  
  const [step, setStep] = useState<AuthStep>('identification');
  const [document, setDocument] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo | null>(null);
  const [sentEmail, setSentEmail] = useState('');

  // Redirect if already authenticated
  useEffect(() => {
    if (user && portalUser) {
      const from = (location.state as any)?.from?.pathname || '/portal/home';
      navigate(from, { replace: true });
    }
  }, [user, portalUser, navigate, location]);

  const handleDocumentLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const cleanDocument = document.replace(/\D/g, '');
      
      if (cleanDocument.length !== 11 && cleanDocument.length !== 14) {
        setError('Digite um CPF ou CNPJ válido');
        setLoading(false);
        return;
      }

      const { data, error: fnError } = await supabase.functions.invoke('portal-customer-lookup', {
        body: { document_number: cleanDocument }
      });

      if (fnError) {
        console.error('Lookup error:', fnError);
        setError('Erro ao verificar documento. Tente novamente.');
        setLoading(false);
        return;
      }

      if (!data.success || !data.found) {
        setError(data.error || 'Cliente não encontrado');
        setLoading(false);
        return;
      }

      setCustomerInfo(data.customer);

      // Decide next step based on onboarding status
      if (data.customer.onboarding_status === 'completed' && data.customer.has_user_id) {
        setStep('login');
      } else {
        setStep('activation');
      }

    } catch (err: any) {
      console.error('Error:', err);
      setError(err.message || 'Erro ao verificar documento');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const result = await signIn(email, password);
    
    if (!result.success) {
      setError(result.error || 'Erro ao fazer login');
    }
    
    setLoading(false);
  };

  const handleActivation = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (!customerInfo) {
        setError('Dados do cliente não encontrados');
        setLoading(false);
        return;
      }

      const { data, error: fnError } = await supabase.functions.invoke('portal-customer-onboarding', {
        body: { 
          customer_id: customerInfo.id,
          email: email
        }
      });

      if (fnError) {
        console.error('Onboarding error:', fnError);
        setError('Erro ao criar acesso. Tente novamente.');
        setLoading(false);
        return;
      }

      if (!data.success) {
        setError(data.error || 'Erro ao criar acesso');
        setLoading(false);
        return;
      }

      setSentEmail(email);
      setStep('confirmation');

    } catch (err: any) {
      console.error('Error:', err);
      setError(err.message || 'Erro ao criar acesso');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setError(null);
    if (step === 'login' || step === 'activation') {
      setStep('identification');
      setCustomerInfo(null);
      setEmail('');
      setPassword('');
    } else if (step === 'confirmation') {
      setStep('login');
      setEmail(sentEmail);
      setPassword('');
    }
  };

  const isLoading = loading || authLoading;

  // Step 1: Identification
  if (step === 'identification') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted p-4">
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-2">
              <User className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-xl">Portal do Cliente</CardTitle>
            <CardDescription>
              Digite seu CPF ou CNPJ para acessar
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleDocumentLookup} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="document">CPF ou CNPJ</Label>
                <Input
                  id="document"
                  type="text"
                  inputMode="numeric"
                  placeholder="000.000.000-00"
                  value={document}
                  onChange={(e) => setDocument(formatDocument(e.target.value))}
                  maxLength={18}
                  required
                  disabled={isLoading}
                  autoFocus
                />
              </div>

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Verificando...
                  </>
                ) : (
                  'Continuar'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-xs text-muted-foreground">
                Problemas para acessar?{' '}
                <span className="text-primary">Entre em contato com a empresa.</span>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step 2a: Login (for completed onboarding)
  if (step === 'login') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted p-4">
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center">
            <Button 
              variant="ghost" 
              size="sm" 
              className="absolute left-4 top-4"
              onClick={handleBack}
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Voltar
            </Button>
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-2">
              <Building2 className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-xl">Olá, {customerInfo?.name}!</CardTitle>
            <CardDescription>
              Faça login para acessar o portal de {customerInfo?.company_name}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">E-mail</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="seu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    required
                    disabled={isLoading}
                    autoFocus
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Senha</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10"
                    required
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Entrando...
                  </>
                ) : (
                  'Entrar'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step 2b: Activation (for new users)
  if (step === 'activation') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted p-4">
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center">
            <Button 
              variant="ghost" 
              size="sm" 
              className="absolute left-4 top-4"
              onClick={handleBack}
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Voltar
            </Button>
            <div className="h-12 w-12 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-2">
              <Mail className="h-6 w-6 text-green-600" />
            </div>
            <CardTitle className="text-xl">Olá, {customerInfo?.name}!</CardTitle>
            <CardDescription>
              Para ativar seu acesso ao portal de {customerInfo?.company_name}, informe seu email
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleActivation} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">Seu melhor e-mail</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="seu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    required
                    disabled={isLoading}
                    autoFocus
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Você receberá uma senha temporária neste email
                </p>
              </div>

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Criando acesso...
                  </>
                ) : (
                  'Solicitar Acesso'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step 3: Confirmation
  if (step === 'confirmation') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted p-4">
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center">
            <div className="h-16 w-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
            <CardTitle className="text-xl">Email enviado!</CardTitle>
            <CardDescription className="mt-2">
              Enviamos uma senha temporária para<br />
              <span className="font-medium text-foreground">{sentEmail}</span>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4 text-center">
              <p className="text-sm text-muted-foreground">
                Verifique sua caixa de entrada e spam.<br />
                Use a senha temporária para fazer login.
              </p>
            </div>

            <Button 
              className="w-full" 
              onClick={() => {
                setStep('login');
                setEmail(sentEmail);
                setPassword('');
              }}
            >
              Já recebi, fazer login
            </Button>

            <Button 
              variant="ghost" 
              className="w-full"
              onClick={handleBack}
            >
              Usar outro email
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
};

export default PortalAuth;
